import { useState, useCallback } from 'react';
import { Button, FormField, Icon } from '@chikara/ui';
import { isValidEmail } from '@chikara/utils';
import { PersonalInfoStepProps, UserSelfOnboardPayload } from '@/features/onboarding/types/onboarding.types';
import { useUserSelfOnboard } from '@/features/onboarding/hooks/useUserSelfOnboard';
import styles from '../Onboarding.module.css';
import { t } from '@/i18n';


// Country code → expected subscriber digits (mirrors team UserModal)
const COUNTRY_SUBSCRIBER_DIGITS: Array<{ prefix: string; subscriberDigits: number }> = [
    { prefix: '52',  subscriberDigits: 10 }, // Mexico
    { prefix: '1',   subscriberDigits: 10 }, // USA / Canada
    { prefix: '34',  subscriberDigits: 9  }, // Spain
    { prefix: '57',  subscriberDigits: 10 }, // Colombia
    { prefix: '54',  subscriberDigits: 10 }, // Argentina
    { prefix: '55',  subscriberDigits: 11 }, // Brazil
    { prefix: '56',  subscriberDigits: 9  }, // Chile
    { prefix: '51',  subscriberDigits: 9  }, // Peru
    { prefix: '44',  subscriberDigits: 10 }, // UK
    { prefix: '49',  subscriberDigits: 10 }, // Germany
    { prefix: '33',  subscriberDigits: 9  }, // France
];

function validatePhone(phone: string): string | undefined {
    if (!phone) return undefined; // optional — empty is OK
    if (!phone.startsWith('+')) return t('team.validations.phoneInvalid');
    if (!/^\+\d+$/.test(phone)) return t('team.validations.phoneInvalid');
    const digits = phone.slice(1);
    if (digits.length < 10 || digits.length > 15) return t('team.validations.phoneInvalid');
    const match = COUNTRY_SUBSCRIBER_DIGITS.find((c) => digits.startsWith(c.prefix));
    if (match) {
        const expected = match.prefix.length + match.subscriberDigits;
        if (digits.length !== expected) return t('team.validations.phoneInvalidLength');
    }
    return undefined;
}

export function PersonalInfoStep({ customerId, onSuccess }: PersonalInfoStepProps) {
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [motherLastName, setMotherLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [emailError, setEmailError] = useState<string | undefined>();
    const [phoneError, setPhoneError] = useState<string | undefined>();

    const { selfOnboard, isLoading, error, clearError } = useUserSelfOnboard();

    const trimmedEmail = email.trim();
    const isFormValid = isValidEmail(trimmedEmail) && !validatePhone(phone) && !isLoading;

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        if (emailError) setEmailError(undefined);
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPhone(e.target.value);
        if (phoneError) setPhoneError(undefined);
    };

    const handlePhoneBlur = useCallback(() => {
        setPhoneError(validatePhone(phone));
    }, [phone]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!trimmedEmail) {
            setEmailError(t('onboarding.personalInfo.errors.emailRequired'));
            return;
        }
        if (!isValidEmail(trimmedEmail)) {
            setEmailError(t('onboarding.personalInfo.errors.emailInvalid'));
            return;
        }
        const phoneValidationError = validatePhone(phone);
        if (phoneValidationError) {
            setPhoneError(phoneValidationError);
            return;
        }
        clearError();

        const payload: UserSelfOnboardPayload = {
            customer_id: customerId,
            email: trimmedEmail,
        };
        if (firstName.trim()) payload.name = firstName.trim();
        if (lastName.trim()) payload.lname = lastName.trim();
        if (motherLastName.trim()) payload.slname = motherLastName.trim();
        if (phone.trim()) payload.phone = phone.trim();

        const ok = await selfOnboard(payload);
        if (ok) onSuccess();
    };

    return (
        <form onSubmit={handleSubmit} className={styles.formView}>
            <div className={styles.stepContainer}>
                <h2 className={styles.title}>{t('onboarding.createAccount.title')}</h2>
                <p className={styles.subtitle}>{t('onboarding.createAccount.subtitle')}</p>
            </div>

            <FormField
                label={t('onboarding.personalInfo.email')}
                type="email"
                value={email}
                onChange={handleEmailChange}
                error={emailError}
                required
            />

            <FormField
                label={t('onboarding.personalInfo.firstName')}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
            />

            <FormField
                label={t('onboarding.personalInfo.lastName')}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
            />

            <FormField
                label={t('onboarding.personalInfo.motherLastName')}
                value={motherLastName}
                onChange={(e) => setMotherLastName(e.target.value)}
            />

            <FormField
                label={t('onboarding.personalInfo.phone')}
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                onBlur={handlePhoneBlur}
                placeholder="+525512345678"
                maxLength={16}
                error={phoneError}
                hint={t('team.validations.phoneHelperText')}
            />

            {error && (
                <p className={styles.inlineError} role="alert">
                    <Icon name="warning" size="sm" color="error" />
                    <span>{error}</span>
                </p>
            )}

            <Button type="submit" disabled={!isFormValid} isLoading={isLoading} className={styles.submitButton}>
                {t('onboarding.action')}
            </Button>
        </form>
    );
}
